import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Container,
  Form,
  Input,
  Label,
  Row,
  Spinner,
  Table,
} from "reactstrap";
import { useTranslation } from "react-i18next";
import { toast, ToastContainer } from "react-toastify";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import PageHelpLink from "../../../Components/Common/PageHelpLink";
import { helpUrlForRoute } from "../../../constants/helpRouteMap";
import {
  createRssFeed,
  deleteRssFeed,
  getRssFeeds,
  previewRssFeed,
  pollRssFeed,
  RssFeedPreview,
  RssFeed,
  RssFeedMode,
  updateRssFeed,
} from "../../../helpers/rss_feeds_helper";

const RssFeedsList = () => {
  const { t, i18n } = useTranslation();
  const [feeds, setFeeds] = useState<RssFeed[]>([]);
  const [name, setName] = useState("");
  const [feedUrl, setFeedUrl] = useState("");
  const [mode, setMode] = useState<RssFeedMode>("draft");
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | "create" | null>(null);
  const [error, setError] = useState<string | null>(null);
  // LAC: pré-visualiza HTML do RSS antes de criar rascunho/campanha.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState<RssFeedPreview | null>(null);
  const [previewFeedId, setPreviewFeedId] = useState<number | null>(null);
  const [previewMode, setPreviewMode] = useState<RssFeedMode>("draft");

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setFeeds((await getRssFeeds()).data || []);
    } catch (e: any) {
      setError(e?.message || t("rss.error.load"));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async (event: React.FormEvent) => {
    event.preventDefault();
    setBusyId("create");
    try {
      const res = await createRssFeed({
        name: name.trim(),
        feed_url: feedUrl.trim(),
        mode,
        is_active: true,
      });
      setFeeds((rows) => [res.data, ...rows]);
      setName("");
      setFeedUrl("");
      toast.success(res.message || t("rss.success.created"));
    } catch (e: any) {
      toast.error(e?.message || t("rss.error.save"));
    } finally {
      setBusyId(null);
    }
  };

  const patch = async (feed: RssFeed, payload: Partial<RssFeed>) => {
    setBusyId(feed.id);
    try {
      const res = await updateRssFeed(feed.id, payload);
      setFeeds((rows) => rows.map((row) => (row.id === feed.id ? res.data : row)));
      toast.success(res.message || t("rss.success.updated"));
    } catch (e: any) {
      toast.error(e?.message || t("rss.error.save"));
    } finally {
      setBusyId(null);
    }
  };

  const openPreview = async (feed: RssFeed) => {
    setBusyId(feed.id);
    try {
      const res = await previewRssFeed(feed.id);
      if (!res?.success) {
        toast.error(res.message || t("rss.error.poll"));
        return;
      }

      const data = res.data ?? null;
      if (!data) {
        toast.info(t("rss.preview.empty"));
        await load();
        return;
      }

      setPreviewMode(feed.mode);
      setPreviewFeedId(feed.id);
      setPreviewData(data);
      setPreviewOpen(true);
    } catch (e: any) {
      toast.error(e?.message || t("rss.error.poll"));
    } finally {
      setBusyId(null);
    }
  };

  const confirmPreview = async () => {
    if (!previewFeedId) return;
    setBusyId(previewFeedId);
    try {
      const res = await pollRssFeed(previewFeedId);
      toast.success(res.message || t("rss.success.polled"));
      setPreviewOpen(false);
      setPreviewData(null);
      setPreviewFeedId(null);
      await load();
    } catch (e: any) {
      toast.error(e?.message || t("rss.error.poll"));
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (feed: RssFeed) => {
    if (!window.confirm(t("rss.confirmDelete", { name: feed.name }))) return;
    setBusyId(feed.id);
    try {
      const res = await deleteRssFeed(feed.id);
      setFeeds((rows) => rows.filter((row) => row.id !== feed.id));
      toast.success(res.message || t("rss.success.deleted"));
    } catch (e: any) {
      toast.error(e?.message || t("rss.error.delete"));
    } finally {
      setBusyId(null);
    }
  };

  document.title = t("rss.documentTitle");
  const helpHref = helpUrlForRoute("/rss-feeds", i18n.language);

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={t("rss.title")} actions={<PageHelpLink href={helpHref} label={t("common.help")} />} />
        <ToastContainer closeButton={false} limit={3} />
        <Row>
          <Col xl={4}>
            <Card>
              <CardHeader><h5 className="card-title mb-0">{t("rss.createTitle")}</h5></CardHeader>
              <CardBody>
                <p className="text-muted">{t("rss.createHelp")}</p>
                <Form onSubmit={create}>
                  <div className="mb-3"><Label htmlFor="rss-name">{t("rss.name")}</Label><Input id="rss-name" required maxLength={120} value={name} onChange={(e) => setName(e.target.value)} /></div>
                  <div className="mb-3"><Label htmlFor="rss-url">{t("rss.url")}</Label><Input id="rss-url" required type="url" placeholder="https://" value={feedUrl} onChange={(e) => setFeedUrl(e.target.value)} /></div>
                  <div className="mb-3"><Label htmlFor="rss-mode">{t("rss.mode")}</Label><Input id="rss-mode" type="select" value={mode} onChange={(e) => setMode(e.target.value as RssFeedMode)}><option value="draft">{t("rss.mode.draft")}</option><option value="auto">{t("rss.mode.auto")}</option></Input><div className="form-text">{t("rss.modeHelp")}</div></div>
                  <Button color="primary" disabled={busyId !== null}>{busyId === "create" ? <Spinner size="sm" /> : t("rss.create")}</Button>
                </Form>
              </CardBody>
            </Card>
          </Col>
          <Col xl={8}>
            {error && <Alert color="danger">{error} <Button color="link" size="sm" onClick={() => void load()}>{t("common.retry")}</Button></Alert>}
            <Card>
              <CardHeader><h5 className="card-title mb-0">{t("rss.listTitle")}</h5></CardHeader>
              <CardBody className={feeds.length ? "p-0" : ""}>
                {loading ? <div className="text-center py-5"><Spinner color="primary" /></div> : feeds.length === 0 ? (
                  <div className="text-center py-5"><i className="ri-rss-line fs-1 text-muted" /><h5>{t("rss.empty.title")}</h5><p className="text-muted mb-0">{t("rss.empty.help")}</p></div>
                ) : (
                  <div className="table-responsive"><Table className="align-middle mb-0"><thead className="table-light"><tr><th>{t("rss.feed")}</th><th>{t("rss.mode")}</th><th>{t("rss.status")}</th><th /></tr></thead><tbody>
                    {feeds.map((feed) => <tr key={feed.id}><td><strong>{feed.name}</strong><div className="small text-muted text-truncate" style={{ maxWidth: 330 }}>{feed.feed_url}</div>{feed.last_error_message && <div className="small text-danger">{t("rss.lastError")}: {feed.last_error_message}</div>}</td><td><Badge color={feed.mode === "auto" ? "info" : "secondary"}>{t(`rss.mode.${feed.mode}`)}</Badge></td><td><div className="form-check form-switch"><Input className="form-check-input" type="switch" checked={feed.is_active} disabled={busyId !== null} onChange={() => void patch(feed, { is_active: !feed.is_active })} /><Label className="form-check-label">{t(feed.is_active ? "rss.active" : "rss.inactive")}</Label></div></td><td className="text-end"><div className="d-flex justify-content-end gap-2"><Button color="soft-primary" size="sm" disabled={busyId !== null} onClick={() => void openPreview(feed)}>{busyId === feed.id ? <Spinner size="sm" /> : t("rss.poll")}</Button><Button color="soft-danger" size="sm" disabled={busyId !== null} onClick={() => void remove(feed)}>{t("common.remove")}</Button></div></td></tr>)}
                  </tbody></Table></div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>

      <Modal
        isOpen={previewOpen}
        toggle={() => {
          if (busyId === null) {
            setPreviewOpen(false);
          }
        }}
        centered
        size="lg"
      >
        <ModalHeader
          toggle={() => {
            if (busyId === null) {
              setPreviewOpen(false);
            }
          }}
        >
          {t("rss.preview.modalTitle")}
        </ModalHeader>
        <ModalBody>
          {previewData ? (
            <>
              <div className="mb-3">
                <h5 className="mb-1">{previewData.title}</h5>
                <p className="text-muted mb-2">
                  <strong>{t("rss.preview.subjectLabel")}:</strong>{" "}
                  {previewData.subject || "—"}
                </p>
                {previewData.item_url ? (
                  <div className="text-muted small text-break">
                    <a
                      href={previewData.item_url}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {previewData.item_url}
                    </a>
                  </div>
                ) : null}
              </div>
              <Alert color="info" className="py-2">
                {t("rss.preview.sanitizedNote")}
              </Alert>
              <div
                className="border rounded bg-white"
                style={{ maxHeight: 480, overflow: "auto" }}
              >
                <iframe
                  title={t("rss.preview.modalTitle")}
                  sandbox=""
                  srcDoc={previewData.html_body}
                  style={{ width: "100%", minHeight: 360, border: 0 }}
                />
              </div>
            </>
          ) : (
            <p className="text-muted mb-0">{t("rss.preview.empty")}</p>
          )}
        </ModalBody>
        <ModalFooter>
          <Button
            color="light"
            disabled={busyId !== null}
            onClick={() => setPreviewOpen(false)}
          >
            {t("rss.preview.close")}
          </Button>
          <Button
            color="primary"
            disabled={busyId !== null || !previewFeedId}
            onClick={() => void confirmPreview()}
          >
            {busyId === previewFeedId ? (
              <Spinner size="sm" />
            ) : previewMode === "draft" ? (
              t("rss.preview.confirmDraft")
            ) : (
              t("rss.preview.confirmAuto")
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default RssFeedsList;
